import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { subscriptionApi } from '../apis/subscription.api';
import { stripeApi } from '../apis/stripe.api';
import { Modal, Tabs, Badge, Progress, Text, Group, Stack, Button, Select, NumberInput, Switch } from '@mantine/core';
import { toast } from 'react-toastify';

const PaymentModal = ({ onClose, additionalCost, onConfirm }) => {
  console.log('PaymentModal rendered with props:', { additionalCost, onConfirm });
  return (
    <Modal
      opened={true}
      onClose={onClose}
      title="Additional Users Payment"
      size="md"
    >
      <Stack spacing="md">
        <Text>Additional cost for increased users: ${additionalCost.toFixed(2)}</Text>
        <Group position="right" mt="xl">
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button onClick={() => {
            console.log('Process Payment button clicked');
            console.log('onConfirm is:', onConfirm);
            onConfirm();
          }}>Process Payment</Button>
        </Group>
      </Stack>
    </Modal>
  );
};

const SubscriptionChange = ({ authToken, organizationId, onClose }) => {
  const [activeTab, setActiveTab] = useState('details');
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    planType: '',
    maxUsers: 0,
    autoRenew: false,
    billingInterval: 'annual'
  });
  const [renewalAmount, setRenewalAmount] = useState(0);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [additionalCost, setAdditionalCost] = useState(0);
  const [initialMaxUsers, setInitialMaxUsers] = useState(0);
  const [dueToday, setDueToday] = useState(0);

  const getQuarterlyPrice = (endDate) => {
    console.log('getQuarterlyPrice called with endDate:', endDate);
    const now = new Date();
    const renewal = new Date(endDate);
    const monthsToRenewal = (renewal.getFullYear() - now.getFullYear()) * 12 + 
                           (renewal.getMonth() - now.getMonth());
    
    console.log('Quarterly price calculation:', {
      now: now.toISOString(),
      renewal: renewal.toISOString(),
      monthsToRenewal
    });
    
    let price;
    if (monthsToRenewal <= 3) {
      price = Number(process.env.REACT_APP_EXPLORER_PRICE_PER_SEAT_1QTR);
    } else if (monthsToRenewal <= 6) {
      price = Number(process.env.REACT_APP_EXPLORER_PRICE_PER_SEAT_2QTR);
    } else if (monthsToRenewal <= 9) {
      price = Number(process.env.REACT_APP_EXPLORER_PRICE_PER_SEAT_3QTR);
    } else {
      price = Number(process.env.REACT_APP_EXPLORER_PRICE_PER_SEAT_4QTR);
    }
    
    console.log('Returning quarterly price:', price);
    return price;
  };

  useEffect(() => {
    const fetchSubscriptionDetails = async () => {
      try {
        const response = await subscriptionApi.getSubscriptionDetails(authToken, organizationId);
        console.log('response.subscription:', response.subscription);
        setSubscriptionData(response.subscription);
        const initialAmount = response.subscription.billing.amount * 12 * response.subscription.features.maxUsers;
        setRenewalAmount(initialAmount);
        setInitialMaxUsers(response.subscription.features.maxUsers);
        setFormData({
          planType: response.subscription.details.planType,
          maxUsers: response.subscription.features.maxUsers,
          autoRenew: response.subscription.billing.autoRenew,
          billingInterval: response.subscription.billing.interval
        });
        setLoading(false);
      } catch (err) {
        setError('Failed to load subscription details');
        setLoading(false);
      }
    };

    fetchSubscriptionDetails();
  }, [authToken, organizationId]);

  useEffect(() => {
    console.log('useEffect triggered with:', {
      subscriptionData,
      formData,
      initialMaxUsers
    });

    if (subscriptionData && formData.maxUsers) {
      // Use the yearly base price (4 quarter price) of 2400
      const yearlyBasePrice = process.env.REACT_APP_EXPLORER_PRICE_PER_SEAT_4QTR;
      const newAmount = yearlyBasePrice * Math.max(1, Math.min(formData.maxUsers, 9));
      console.log('Calculating renewal amount:', {
        yearlyBasePrice,
        maxUsers: formData.maxUsers,
        newAmount
      });
      setRenewalAmount(newAmount);

      if (formData.maxUsers !== initialMaxUsers) {
        const userDifference = formData.maxUsers - initialMaxUsers;
        console.log('Calculating due today:', {
          userDifference,
          endDate: subscriptionData.details.endDate
        });
        
        // Annual pricing only
        console.log('Using quarterly pricing');
        const quarterlyPrice = getQuarterlyPrice(subscriptionData.details.endDate);
        const dueAmount = quarterlyPrice * userDifference;
        console.log('Quarterly calculation:', {
          quarterlyPrice,
          userDifference,
          dueAmount
        });
        setDueToday(dueAmount);
      } else {
        console.log('No user difference, setting dueToday to 0');
        setDueToday(0);
      }
    }
  }, [formData.maxUsers, subscriptionData, initialMaxUsers]);

  const handleSubmit = async () => {
    if (formData.maxUsers < initialMaxUsers) {
      // Show warning for decreasing users
      if (!window.confirm(`Your current (${initialMaxUsers}) users will be valid until the end of the subscription period. Continue?`)) {
        setFormData({ ...formData, maxUsers: initialMaxUsers });
        return;
      }
      processUpdate();
    } else if (formData.maxUsers > initialMaxUsers) {
      // Calculate additional cost and show payment modal
      const endDate = new Date(subscriptionData.details.endDate);
      const today = new Date();
      const monthsRemaining = (endDate.getFullYear() - today.getFullYear()) * 12 + 
                            (endDate.getMonth() - today.getMonth());
      const additionalUsers = formData.maxUsers - initialMaxUsers;
      const cost = subscriptionData.billing.amount * Math.max(monthsRemaining, 1) * additionalUsers;
      // setAdditionalCost(cost);
      setAdditionalCost(dueToday);
      setShowPaymentModal(true);
    } else {
      processUpdate();
    }
  };

  const processUpdate = async () => {
    try {
      await subscriptionApi.updateSubscription(authToken, organizationId, {
        maxUsers: formData.maxUsers,
        autoRenew: formData.autoRenew,
        billing: {
          interval: 'annual',
          amount: 2300,
          nextBillingDate: subscriptionData.details.endDate
        },
        email: subscriptionData.orgLeader.email
      });
      onClose();
    } catch (err) {
      console.error('Error updating subscription:', err);
      setError('Failed to update subscription terms');
    }
  };

  const handlePaymentConfirm = async () => {
    console.log('handlePaymentConfirm called with:', {
      formData,
      initialMaxUsers,
      subscriptionData,
      additionalCost
    });

    try {
      console.log('Attempting to create upgrade session with data:', {
        seats: formData.maxUsers - initialMaxUsers,
        email: subscriptionData.orgLeader.email,
        userName: subscriptionData.details.userName,
        companyName: organizationId,
        region: subscriptionData.details.region || process.env.REACT_APP_REGION_CODE.toLowerCase(),
        planType: 'explorer',
        paymentSchedule: 'annual',
        isUpgrade: true,
        subscriptionId: subscriptionData.details.id
      });

      const response = await stripeApi.createUpgradeSession({
        seats: formData.maxUsers - initialMaxUsers,
        email: subscriptionData.orgLeader.email,
        userName: subscriptionData.details.userName,
        companyName: organizationId,
        region: subscriptionData.details.region || process.env.REACT_APP_REGION_CODE.toLowerCase(),
        planType: 'explorer',
        paymentSchedule: 'annual',
        isUpgrade: true,
        subscriptionId: subscriptionData.details.id
      });

      console.log('Full Stripe upgrade response:', response);

      if (response?.url) {
        console.log('Opening Stripe window with URL:', response.url);
        const stripeWindow = window.open(response.url, 'stripe', 'width=1000,height=800');
        
        console.log('Setting up window close polling');
        const pollTimer = setInterval(() => {
          console.log('Checking if Stripe window closed');
          if (stripeWindow.closed) {
            console.log('Stripe window detected as closed');
            clearInterval(pollTimer);
            // toast.info(
            //   'Your payment is being processed. If you do not see the changes immediately, then refresh this page to see your updated subscription.',
            //   { autoClose: false }
            // );
            onClose();
          }
        }, 500);
      } else {
        console.error('No URL in response:', response);
        toast.error('Failed to start payment process. Please try again.');
      }
    } catch (error) {
      console.error('Detailed payment processing error:', {
        error,
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      toast.error('Failed to process payment. Please try again.');
    }
  };

  const formatDate = (dateString) => {
    return format(new Date(dateString), 'MMM dd, yyyy');
  };

  return (
    <>
      <Modal
        opened={true}
        onClose={onClose}
        title="Change Subscription"
        size="lg"
        styles={{
          header: {
            backgroundColor: '#f8f9fa',
            padding: '16px 24px',
            marginBottom: '16px',
          },
          title: {
            fontWeight: 600,
            fontSize: '1.1rem',
          }
        }}
      >
        {loading ? (
          <Text p="md">Loading subscription details...</Text>
        ) : error ? (
          <Text color="red" p="md">{error}</Text>
        ) : subscriptionData && (
          <>
            <Tabs defaultValue="details">
              <Tabs.List>
                <Tabs.Tab value="details">Plan Details</Tabs.Tab>
                <Tabs.Tab value="billing">Billing</Tabs.Tab>
                {false && <Tabs.Tab value="features">Features</Tabs.Tab>}
              </Tabs.List>

              <Tabs.Panel value="details" pt="md">
                <Stack spacing="md">
                  <Group position="apart">
                    <div style={{ flex: 1 }}>
                      <Text size="sm" weight={500} color="dimmed">Plan Type</Text>
                      <Badge 
                        size="lg"
                        variant="light"
                        color={formData.planType === 'enterprise' ? 'blue' : 'green'}
                      >
                        {formData.planType === 'enterprise' ? 
                          'Enterprise (10+ users, 1yr)' : 
                          'Explorer (1-9 users, 1yr)'}
                      </Badge>
                    </div>
                    <div>
                      <Text size="sm" weight={500} color="dimmed">Status</Text>
                      <Badge 
                        color={subscriptionData.details.status === 'active' ? 'green' : 'red'}
                        variant="light"
                      >
                        {subscriptionData.details.status}
                      </Badge>
                    </div>
                  </Group>

                  <div>
                    <Text size="sm" weight={500} color="dimmed">MaxUsers</Text>
                    {formData.planType === 'enterprise' ? (
                      <Text>{formData.maxUsers}</Text>
                    ) : (
                      <>
                        <NumberInput
                          value={formData.maxUsers}
                          onChange={(value) => setFormData({ ...formData, maxUsers: value })}
                          min={1}
                          max={9}
                          placeholder="Enter number of users (1-9)"
                        />
                        <Text size="xs" color="dimmed" mt={4}>
                          <a 
                            href="mailto:sales@lavenirai.com?subject=Enterprise%20Plan%20Inquiry"
                            style={{ color: 'inherit', textDecoration: 'underline' }}
                          >
                            For more than 9 users, contact us about an Enterprise Plan
                          </a>
                        </Text>
                      </>
                    )}
                  </div>

                  <Switch
                    label="Auto Renew"
                    checked={formData.autoRenew}
                    onChange={(event) => {
                      const newAutoRenew = event.currentTarget.checked;
                      setFormData({ ...formData, autoRenew: newAutoRenew });
                      
                      // Add toast notification when autoRenew is turned off
                      if (!newAutoRenew) {
                        toast.info("Contact support@lavenirai.com to leave this organization.", {
                          autoClose: 6000
                        });
                      }
                    }}
                  />

                  <div>
                    <Text size="sm" weight={500} color="dimmed">Subscription Period</Text>
                    <Group position="apart">
                      <Stack spacing={4}>
                        <Text>
                          {formatDate(subscriptionData.details.startDate)} - {formatDate(subscriptionData.details.endDate)}
                        </Text>
                        {formData.maxUsers !== initialMaxUsers && (
                          <Text size="sm" color="dimmed" mt={20}>
                            Additional Users: {formData.maxUsers > initialMaxUsers ? '+' : ''}{formData.maxUsers - initialMaxUsers}
                          </Text>
                        )}
                      </Stack>
                      <Stack spacing={4}>
                        <Text weight={500} color="blue">
                          Annual Renewal: ${renewalAmount.toFixed(2)}
                        </Text>
                        {formData.maxUsers !== initialMaxUsers && (
                          <Text weight={500} color="red">
                            Due Today: ${dueToday.toFixed(2)}
                          </Text>
                        )}
                      </Stack>
                    </Group>
                  </div>
                </Stack>
              </Tabs.Panel>

              <Tabs.Panel value="billing" pt="md">
                <Stack spacing="md">
                  <Group position="apart">
                    <div>
                      <Text size="sm" weight={500} color="dimmed">Billing Interval</Text>
                      <Text>Annual</Text>
                    </div>
                    <div>
                      <Text size="sm" weight={500} color="dimmed">Amount</Text>
                      <Text>
                        {subscriptionData.billing.currency} {subscriptionData.billing.amount}
                      </Text>
                    </div>
                  </Group>
                  <div>
                    <Text size="sm" weight={500} color="dimmed">Next Billing</Text>
                    <Text>
                      {false && formatDate(subscriptionData.billing.nextBillingDate)}
                      {formatDate(subscriptionData.details.endDate)}
                    </Text>
                    <Text size="sm" color="dimmed">
                      ({Math.ceil((new Date(subscriptionData.details.endDate) - new Date()) / (1000 * 60 * 60 * 24))} days remaining)
                    </Text>
                  </div>
                </Stack>
              </Tabs.Panel>

              <Tabs.Panel value="features" pt="md">
                <Stack spacing="md">
                  <div>
                    <Text size="sm" weight={500} color="dimmed">Credit Utilization</Text>
                    <Group position="apart" mt="xs">
                      <Text size="sm">
                        {subscriptionData.features.currentUsers} of {subscriptionData.features.maxUsers} credits
                      </Text>
                      <Text size="sm">{subscriptionData.features.utilizationPercentage}%</Text>
                    </Group>
                    <Progress 
                      value={subscriptionData.features.utilizationPercentage} 
                      mt="xs"
                      size="md"
                    />
                  </div>
                  <div>
                    <Text size="sm" weight={500} color="dimmed">Allowed Features</Text>
                    <Group spacing="xs" mt="xs">
                      {subscriptionData.features.allowedFeatures.map((feature, index) => (
                        <Badge key={index} variant="outline">
                          {feature}
                        </Badge>
                      ))}
                    </Group>
                  </div>
                </Stack>
              </Tabs.Panel>
            </Tabs>

            <Group position="right" mt="xl">
              <Button variant="default" onClick={onClose}>Cancel</Button>
              <Button onClick={handleSubmit}>Update Subscription</Button>
            </Group>
          </>
        )}
      </Modal>

      {showPaymentModal && (
        <PaymentModal
          additionalCost={additionalCost}
          onClose={() => {
            console.log('PaymentModal close triggered');
            setFormData({ ...formData, maxUsers: initialMaxUsers });
            setShowPaymentModal(false);
          }}
          onConfirm={() => {
            console.log('PaymentModal confirm triggered');
            handlePaymentConfirm();
          }}
        />
      )}
    </>
  );
};

export default SubscriptionChange;